import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, Lock, User, Mail, Building2, FileText, Phone } from 'lucide-react';
import { apiClient } from '../lib/api';
import { PhysicianOnboardingWizard } from '../components/physicians/PhysicianOnboardingWizard';

interface RegistrationData {
  invitationToken: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'physician' | 'distributor';
  password: string;
  confirmPassword: string;
  phone: string;
  npiNumber?: string;
  // Self-service: clinic has no NPI and wants to prescribe through the doctor
  // network (an admin assigns the actual network after registration).
  telemedNetworkRequested?: boolean;
  driversLicense?: string;
  clinicName?: string;
  clinicAddress?: {
    street: string;
    street2?: string;
    city: string;
    state: string;
    zipCode: string;
    country?: string;
  };
  companyName?: string;
  address?: {
    street: string;
    street2?: string;
    city: string;
    state: string;
    zipCode: string;
    country?: string;
  };
  referralSource?: string;
  productInterest?: string;
}

interface InvitationData {
  email: string;
  firstName?: string;
  lastName?: string;
  role: 'physician' | 'distributor';
  // Internal sales rep — no company (they're an employee).
  salesRepEnabled?: boolean;
  // Telemed-only clinic: no NPI of its own (the assigned network prescribes).
  telemedOnly?: boolean;
  // Pre-fill fields from invitation
  npiNumber?: string;
  phone?: string;
  clinicName?: string;
  driversLicense?: string;
  clinicAddress?: {
    street?: string;
    street2?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  };
}

export const Register: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [invitation, setInvitation] = useState<InvitationData | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tokenValidating, setTokenValidating] = useState(true);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState<RegistrationData>({
    invitationToken: '',
    email: '',
    firstName: '',
    lastName: '',
    role: 'physician',
    password: '',
    confirmPassword: '',
    phone: '',
    npiNumber: '',
    telemedNetworkRequested: false,
    driversLicense: '',
    clinicName: '',
    clinicAddress: {
      street: '',
      street2: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'US'
    },
    companyName: '',
    address: {
      street: '',
      street2: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'US'
    },
    referralSource: '',
    productInterest: ''
  });

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      // No token = self-signup mode
      setTokenValidating(false);
      return;
    }

    // Decode JWT token to get invitation data (basic validation)
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.type === 'user-invitation') {
        // Extract pre-fill data from invitation token
        const invitationData: InvitationData = {
          email: payload.email,
          firstName: payload.firstName,
          lastName: payload.lastName,
          role: payload.role,
          salesRepEnabled: payload.salesRepEnabled,
          telemedOnly: payload.telemedOnly,
          npiNumber: payload.npiNumber,
          phone: payload.phone,
          clinicName: payload.clinicName,
          driversLicense: payload.driversLicense,
          clinicAddress: payload.clinicAddress
        };
        setInvitation(invitationData);

        // Pre-populate form with invitation data
        setFormData(prev => ({
          ...prev,
          invitationToken: token,
          npiNumber: payload.npiNumber || '',
          phone: payload.phone || '',
          driversLicense: payload.driversLicense || '',
          clinicName: payload.clinicName || '',
          clinicAddress: {
            street: payload.clinicAddress?.street || '',
            street2: payload.clinicAddress?.street2 || '',
            city: payload.clinicAddress?.city || '',
            state: payload.clinicAddress?.state || '',
            zipCode: payload.clinicAddress?.zipCode || '',
            country: 'US'
          }
        }));
      } else {
        setError('Invalid invitation token');
      }
    } catch (err) {
      setError('Invalid invitation token format');
    } finally {
      setTokenValidating(false);
    }
  }, [searchParams]);

  const validateForm = (): boolean => {
    // For self-signup, validate email, firstName, lastName
    if (!invitation) {
      if (!formData.email.trim()) {
        setError('Email is required');
        return false;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        setError('Please enter a valid email address');
        return false;
      }
      if (!formData.firstName.trim()) {
        setError('First name is required');
        return false;
      }
      if (!formData.lastName.trim()) {
        setError('Last name is required');
        return false;
      }
    }

    if (!formData.phone || !formData.phone.trim()) {
      setError('Phone number is required');
      return false;
    }

    if (!formData.password || formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    const userRole = invitation?.role || formData.role;
    if (userRole === 'physician') {
      // NPI is waived for a pre-provisioned telemed-only clinic (admin-set on the
      // invite) or when the user self-declares they'll use the doctor network.
      const noNpi = invitation?.telemedOnly === true || formData.telemedNetworkRequested === true;
      if ((!formData.npiNumber && !noNpi) || !formData.driversLicense) {
        setError(noNpi
          ? 'Driver\'s License is required for physicians'
          : 'NPI Number and Driver\'s License are required for physicians');
        return false;
      }
      if (!formData.clinicName?.trim()) {
        setError('Clinic name is required for physicians');
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      let response;
      
      if (invitation) {
        // Invitation flow - auto-approved
        const registrationData: any = {
          invitationToken: formData.invitationToken,
          password: formData.password,
          phone: formData.phone.trim(),
          ...(formData.referralSource && { referralSource: formData.referralSource }),
          ...(formData.productInterest && { productInterest: formData.productInterest })
        };

        if (invitation.role === 'physician') {
          // Self-declared no-NPI clinic (only when the invite didn't already
          // provision a full telemed-only clinic).
          const requested = !invitation.telemedOnly && formData.telemedNetworkRequested === true;
          if (requested) registrationData.telemedNetworkRequested = true;
          // Omit NPI for telemed-only clinics or self-declared network clinics.
          if (formData.npiNumber && !invitation.telemedOnly && !requested) {
            registrationData.npiNumber = formData.npiNumber;
          }
          registrationData.driversLicense = formData.driversLicense;
          registrationData.clinicName = formData.clinicName;
          registrationData.clinicAddress = formData.clinicAddress;
        } else if (invitation.role === 'distributor') {
          registrationData.companyName = formData.companyName;
          registrationData.address = formData.address;
        }

        response = await apiClient.post('/auth/complete-registration', registrationData);
        
        if (response.success) {
          navigate('/login?message=Registration completed successfully. Your account is now active and ready to use!');
        }
      } else {
        // Self-signup flow - requires approval
        const registrationData: any = {
          email: formData.email.trim(),
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          role: formData.role,
          password: formData.password,
          phone: formData.phone.trim(),
          ...(formData.referralSource && { referralSource: formData.referralSource }),
          ...(formData.productInterest && { productInterest: formData.productInterest })
        };

        if (formData.role === 'physician') {
          // Self-declared no-NPI clinic wanting the doctor network.
          if (formData.telemedNetworkRequested) {
            registrationData.telemedNetworkRequested = true;
          } else {
            registrationData.npiNumber = formData.npiNumber;
          }
          registrationData.driversLicense = formData.driversLicense;
          registrationData.clinicName = formData.clinicName;
          registrationData.clinicAddress = formData.clinicAddress;
        } else if (formData.role === 'distributor') {
          registrationData.companyName = formData.companyName;
          registrationData.address = formData.address;
        }

        response = await apiClient.post('/auth/register', registrationData);
        
        if (response.success) {
          navigate('/login?message=Registration request submitted successfully. Please wait for admin approval before signing in.');
        }
      }

      if (!response.success) {
        setError(response.error || 'Registration failed');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string | boolean, section?: string) => {
    setFormData(prev => {
      if (section) {
        return {
          ...prev,
          [section]: {
            ...(prev as any)[section],
            [field]: value
          }
        };
      }
      return { ...prev, [field]: value };
    });
  };

  if (tokenValidating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-center mt-4 text-gray-600">Validating invitation...</p>
        </div>
      </div>
    );
  }

  // Only show error if it's not about missing token
  if (error && invitation === null && formData.invitationToken) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full mx-4">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Invalid Invitation</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <Link
              to="/login"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Go to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Invited physicians go through the dedicated onboarding wizard (personal info
  // + SMS 2FA → NPI branch → licenses/network, or no-NPI → network + contract).
  // Non-physician invites and self-signup keep the single-page form below.
  if (invitation && invitation.role === 'physician') {
    return (
      <PhysicianOnboardingWizard
        invitationToken={formData.invitationToken}
        invitation={invitation}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="bg-white rounded-xl shadow-lg p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <User className="w-8 h-8 text-blue-600" />
            </div>
            {invitation ? (
              <>
                <h2 className="text-2xl font-bold text-gray-900">Complete Registration</h2>
                <p className="text-gray-600 mt-2">
                  Welcome, {invitation.firstName}! Complete your {invitation.role} account setup.
                </p>
              </>
            ) : (
              <>
                <h2 className="text-2xl font-bold text-gray-900">Request Account Access</h2>
                <p className="text-gray-600 mt-2">
                  Submit your registration request. An admin will review and approve your account.
                </p>
              </>
            )}
          </div>

          {/* Invitation Info */}
          {invitation && (
            <div className="bg-blue-50 rounded-lg p-4 mb-6">
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="text-sm font-medium text-blue-900">{invitation.email}</p>
                  <p className="text-xs text-blue-700">
                    {invitation.firstName} {invitation.lastName} - {invitation.role}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Self-signup fields */}
            {!invitation && (
              <>
                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address
                  </label>
                  <div className="relative">
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="your.email@company.com"
                      required
                    />
                    <Mail className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                  </div>
                </div>

                {/* Name Fields */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name
                    </label>
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="John"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name
                    </label>
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Smith"
                      required
                    />
                  </div>
                </div>

                {/* Role Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Role
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) => handleInputChange('role', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="physician">Physician</option>
                    <option value="distributor">Account Manager</option>
                  </select>
                </div>
              </>
            )}

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter password (min 8 characters)"
                  required
                />
                <Lock className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                  className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Confirm your password"
                  required
                />
                <Lock className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* Phone Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number
              </label>
              <div className="relative">
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="+1-555-555-5555"
                  required
                />
                <Phone className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              </div>
            </div>

            {/* Role-specific fields */}
            {(invitation?.role === 'physician' || (!invitation && formData.role === 'physician')) && (
              <>
                {invitation?.telemedOnly ? (
                  <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-sm text-blue-800">
                    This clinic prescribes through a telemed network, so no NPI is required.
                  </div>
                ) : (
                  <>
                    {/* Self-service: no NPI, prescribe through the doctor network.
                        The account is created without an NPI; an admin connects it
                        to a prescribing network before it can place orders. */}
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!formData.telemedNetworkRequested}
                        onChange={(e) => handleInputChange('telemedNetworkRequested', e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">
                        I don't have an NPI — I'll prescribe through the doctor network
                        <span className="block text-xs text-gray-500">
                          Our team will connect your clinic to a prescribing network before you can place orders.
                        </span>
                      </span>
                    </label>

                    {!formData.telemedNetworkRequested && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          NPI Number
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            value={formData.npiNumber}
                            onChange={(e) => handleInputChange('npiNumber', e.target.value)}
                            className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="National Provider Identifier"
                            required
                          />
                          <FileText className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                        </div>
                      </div>
                    )}
                  </>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Driver's License
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={formData.driversLicense}
                      onChange={(e) => handleInputChange('driversLicense', e.target.value)}
                      className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Driver's License Number"
                      required
                    />
                    <FileText className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                  </div>
                </div>

                {/* Clinic Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Clinic / Practice Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={formData.clinicName || ''}
                      onChange={(e) => handleInputChange('clinicName', e.target.value)}
                      className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., Downtown Medical Clinic"
                    />
                    <Building2 className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                  </div>
                </div>

                {/* Clinic Address */}
                <div className="space-y-4">
                  <h4 className="text-sm font-medium text-gray-700">Clinic Address</h4>
                  <div>
                    <input
                      type="text"
                      value={formData.clinicAddress?.street}
                      onChange={(e) => handleInputChange('street', e.target.value, 'clinicAddress')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Street Address"
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      value={formData.clinicAddress?.street2}
                      onChange={(e) => handleInputChange('street2', e.target.value, 'clinicAddress')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Suite, Unit, Building (optional)"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="text"
                      value={formData.clinicAddress?.city}
                      onChange={(e) => handleInputChange('city', e.target.value, 'clinicAddress')}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="City"
                    />
                    <input
                      type="text"
                      value={formData.clinicAddress?.state}
                      onChange={(e) => handleInputChange('state', e.target.value, 'clinicAddress')}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="State"
                    />
                  </div>
                  <input
                    type="text"
                    value={formData.clinicAddress?.zipCode}
                    onChange={(e) => handleInputChange('zipCode', e.target.value, 'clinicAddress')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="ZIP Code"
                  />
                </div>
              </>
            )}

            {((invitation?.role === 'distributor' && !invitation?.salesRepEnabled) || (!invitation && formData.role === 'distributor')) && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company Name (Optional)
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={formData.companyName}
                      onChange={(e) => handleInputChange('companyName', e.target.value)}
                      className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Your company name"
                    />
                    <Building2 className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                  </div>
                </div>

                {/* Company Address */}
                <div className="space-y-4">
                  <h4 className="text-sm font-medium text-gray-700">Company Address</h4>
                  <div>
                    <input
                      type="text"
                      value={formData.address?.street}
                      onChange={(e) => handleInputChange('street', e.target.value, 'address')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Street Address"
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      value={formData.address?.street2}
                      onChange={(e) => handleInputChange('street2', e.target.value, 'address')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Suite, Unit, Building (optional)"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="text"
                      value={formData.address?.city}
                      onChange={(e) => handleInputChange('city', e.target.value, 'address')}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="City"
                    />
                    <input
                      type="text"
                      value={formData.address?.state}
                      onChange={(e) => handleInputChange('state', e.target.value, 'address')}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="State"
                    />
                  </div>
                  <input
                    type="text"
                    value={formData.address?.zipCode}
                    onChange={(e) => handleInputChange('zipCode', e.target.value, 'address')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="ZIP Code"
                  />
                </div>
              </>
            )}

            {/* Intake Questions */}
            <div className="space-y-4 pt-2 border-t border-gray-200">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  How did you hear about us? <span className="text-gray-400 font-normal">(Optional)</span>
                </label>
                <input
                  type="text"
                  value={formData.referralSource}
                  onChange={(e) => handleInputChange('referralSource', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Google, referral, trade show"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  What types of products and volumes are you interested in? <span className="text-gray-400 font-normal">(Optional)</span>
                </label>
                <textarea
                  value={formData.productInterest}
                  onChange={(e) => handleInputChange('productInterest', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Semaglutide 5mg vials, ~50 units/month"
                  rows={3}
                />
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating Account...' : (invitation ? 'Complete Registration' : 'Submit Request')}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}; 